import React, { useEffect, useReducer, useState } from 'react';
import {
  useHistory, useParams, useLocation, Redirect,
} from 'react-router-dom';
import {
  Box, Container, styled, ThemeProvider,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';

import { useQuery } from '@apollo/client';

import { isValidPhoneNumber } from 'react-phone-number-input';

import reducer, { initialState } from './preOnboardingState';
import { LanguageSelector } from '../Onboarding/components/LanguageSelector/LanguageSelector';
import { CheckYourEmail, SubmitEmail, ErrorState } from './steps';
import Styled from './PreOnboardingPage.styles';
import { WhoAreYouEnrollingActivate } from './steps/WhoAreYouEnrollingActivate';

import { Footer } from '../Onboarding/components/Footer/Footer';

import { ConsentWidget } from '../Onboarding/components/ConsentWidget/ConsentWidget';

import { SleepioWellnessOnboardingParams } from '../SleepioWellnessOnboarding/SleepioWellnessOnboardingPage';

import SleepioLogoSrc from '@/assets/SleepioLogoIcon.svg';
import SparkDirectLogo from '@/assets/SparkDirectLogo.svg';
import theme from '@/themes/sleepioTheme';
import sparkTheme from '@/themes/sparkTheme';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { getProgram } from '@/utils/posthog';
import { EMAIL_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '@/utils/constants';
import { getPdtUid, Product, isSpark as isSparkHelper } from '@/utils/productHelpers';
import { ELIGIBILITY_CONFIGURATION_QUERY } from '@/apollo/queries';
import Loader from '@/pages/SleepioWellnessOnboarding/components/Loader';

export type PreOnboardingParams = {
  slug: string;
  product: Product;
};

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: '100vh',
});

const schema = z.object({
  email: z.string().nonempty().regex(EMAIL_VALIDATION_REGEX),
  phoneNumber: z
    .union([z.literal(''), z.string().regex(PHONE_VALIDATION_REGEX)])
    .optional(),
  termsAndPrivacy: z.boolean().refine((value) => value === true),
  agreeToReceiveTextMessages: z.boolean().optional(),
  marketingConsent: z.boolean().optional(),
});

export type SetupForm = z.infer<typeof schema>;

const PreOnboardingPage = () => {
  useAcceptedLanguage();
  useConsent();
  const posthog = usePostHog();
  const { slug } = useParams<SleepioWellnessOnboardingParams>();
  // It may be good to create an error state for if there is an invalid slug
  const { data, loading } = useQuery(ELIGIBILITY_CONFIGURATION_QUERY,
    { variables: { organizationSlug: slug, pdtUid: getPdtUid('sleepio') } });
  const [state, dispatch] = useReducer(reducer, initialState);
  const { step } = state;
  const { product } = useParams<PreOnboardingParams>();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialAutoSelect = params.get('autoSelect');
  const isSpark = isSparkHelper(product);
  const [showWho, setShowWho] = useState(isSpark && initialAutoSelect !== 'myself');

  useEffect(() => {
    if (posthog) {
      // resetting PostHog to ensure a new session is created as this is the beginning of the flow
      posthog.reset();
      posthog.capture('ActivationFlowStarted', { program: getProgram(product) });
    }
  }, [posthog, product, getProgram]);

  const schemaForProduct = isSpark
    ? z.object({
      email: z.string().nonempty().regex(EMAIL_VALIDATION_REGEX),
      phoneNumber: z.string().refine(
        (v) => isValidPhoneNumber(v || ''),
        { message: 'Please enter a valid phone number' },
      ),
      termsAndPrivacy: z.boolean().refine((value) => value === true),
      agreeToReceiveTextMessages: z.boolean().refine((v) => v === true),
      marketingConsent: z.boolean().optional(),
    })
    : schema;

  const methods = useForm<SetupForm>({
    resolver: zodResolver(schemaForProduct),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: '',
      phoneNumber: '',
      termsAndPrivacy: false,
      agreeToReceiveTextMessages: false,
      marketingConsent: false,
    },
  });

  const renderStep = () => {
    if (isSpark && showWho) {
      return (
        <ThemeProvider theme={sparkTheme}>
          <WhoAreYouEnrollingActivate
            onMyself={() => setShowWho(false)}
            onDependent={() => {
              setShowWho(false);
              history.replace(`/coverage/${product}/${slug}?autoSelect=dependent`);
            }}
          />
        </ThemeProvider>
      );
    }
    switch (step) {
    case 'SubmitEmail':
      if (loading) {
        return <Loader />;
      }
      return (
        <SubmitEmail
          onNext={() => dispatch({ type: 'SET_STEP', step: 'CheckYourEmail' })}
          onError={() => dispatch({ type: 'SET_STEP', step: 'ErrorState' })}
          isGeneric={data?.eligibilityConfiguration?.isGeneric}
        />
      );
    case 'CheckYourEmail':
      if (loading) {
        return <Loader />;
      }
      return <CheckYourEmail isGeneric={data?.eligibilityConfiguration?.isGeneric} />;
    case 'ErrorState':
      return <ErrorState onNext={() => dispatch({ type: 'SET_STEP', step: 'SubmitEmail' })} />;
    default:
      return null;
    }
  };

  if (isSpark && initialAutoSelect === 'myself') {
    const qs = new URLSearchParams(location.search);
    qs.delete('autoSelect');
    const clean = qs.toString();
    return (
      <Redirect
        to={{
          pathname: location.pathname,
          search: clean ? `?${clean}` : '',
          hash: location.hash,
        }}
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ContentContainer>
        <Styled.Header
          style={isSpark
            ? {
              paddingTop: 40,
              paddingBottom: 0,
            } : undefined}
        >
          <Styled.SleepioHeader
            style={isSpark
              ? {
                alignItems: 'center',
                height: 48,
              } : undefined}
          >
            {!isSpark ? (
              <img src={SleepioLogoSrc} height="48px" alt="sleepio logo" />
            ) : (
              <img
                src={SparkDirectLogo}
                alt="spark direct logo"
                height={39.5}
                width={180}
                style={{ display: 'block' }}
              />
            )}
          </Styled.SleepioHeader>
          <LanguageSelector iconOnly={isSpark} />
        </Styled.Header>
        <Container
          maxWidth={isSpark && showWho ? 'sm' : 'xs'}
          style={{ flex: 1 }}
        >
          <FormProvider {...methods}>{renderStep()}</FormProvider>
          <ConsentWidget />
        </Container>
        <Footer />
      </ContentContainer>
    </ThemeProvider>
  );
};

export default PreOnboardingPage;
